import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Clock, Gauge, Home } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import VideoEmbed from "@/components/VideoEmbed";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect } from "react";
import { trackEvent, AnalyticsEvents } from "@/utils/analytics";
import { newGameDetails } from "@/data/newGameDetails";

interface GameData {
  slug: string;
  title: string;
  description: string;
  fullDescription: string;
  image: string;
  youtubeId?: string;
  videoSrc?: string;
  features: string[];
  highlights: string[];
  details: {
    players: string;
    duration: string;
    difficulty: string;
    age: string;
    space: string;
    intensity: string;
  };
}

const gamesData: GameData[] = [
  ...newGameDetails,
  {
    slug: "arizona-sunshine-remake",
    title: "Arizona Sunshine® Remake",
    description: "De originele award-winning VR zombie shooter, volledig herbouwd met next-gen graphics en verbeterd combat systeem",
    fullDescription:
      "Arizona Sunshine® Remake tilt de originele, bekroonde game naar een nieuw niveau — volledig herbouwd met prachtige VR graphics en next-gen combat en wapens. Wanneer je een menselijke stem opvangt via de radio, schiet je hoop omhoog — er zijn overlevenden in de verzengende hitte van het post-apocalyptische Arizona! Gewapend met weinig meer dan je motion-controlled wapens en de schaarse munitie en voorraden die je onderweg verzamelt, moet je je een weg banen door hordes zombies. Met verbeterde dismemberment mechanics, nieuwe wapens en een volledig vernieuwde visuele ervaring is dit de definitieve versie van de klassieke VR zombie shooter.",
    image: "/src/assets/arizona-sunshine-remake.jpg",
    youtubeId: "qJhPlCr2TYM",
    features: [
      "Volledig herbouwd met next-gen VR graphics",
      "Verbeterd combat en wapen systeem",
      "Free-roam multiplayer tot 4 spelers",
      "Post-apocalyptisch Arizona setting",
      "Uitgebreid wapenarsenaal",
      "Intense zombie survival gameplay",
    ],
    highlights: [
      "Award-winning klassieke VR game",
      "Volledig vernieuwde graphics",
      "Intense co-op zombie actie",
      "Free-roam VR ervaring",
    ],
    details: {
      players: "1-4 spelers",
      duration: "30-45 minuten",
      difficulty: "Medium-Hard",
      age: "18+",
      space: "Free-roam speelruimte",
      intensity: "Hoog",
    },
  },
  {
    slug: "the-parvus-box",
    title: "The Parvus Box",
    description: "Word gekrompen tot microscopisch formaat in deze unieke escape room",
    fullDescription:
      "The Parvus Box is een buitengewoon escape room avontuur waarin je onderdeel wordt van een bijzonder experiment. Als moedige vrijwilliger stap je naar voren om een mysterieus apparaat te testen - een apparaat dat je onverwachts krimpt tot microscopisch formaat. In deze meeslepende ervaring transformeren alledaagse omgevingen in een enorm speelveld vol oversized puzzels en onverwachte uitdagingen. Werk nauw samen in teams van 2 tot 6 personen, gebruik je vindingrijkheid en naadloze samenwerking om aanwijzingen te verzamelen en je normale grootte te herstellen. Deze escape room belooft een unieke, onvergetelijke reis vol gelach, snel denken en een gedeeld gevoel van triomf.",
    image: "/src/assets/parvus-box.webp",
    videoSrc: "/videos/parvus-box-trailer.webm",
    features: [
      "2-6 spelers free-roam co-op",
      "Uniek krimp-concept",
      "Oversized puzzel omgeving",
      "Team samenwerking vereist",
      "Kleurrijke cartoonstijl",
      "Familie-vriendelijke gameplay",
    ],
    highlights: ["Unieke krimp ervaring", "Uitdagende puzzels", "Perfect voor families", "Meeslepend verhaal"],
    details: {
      players: "2-6 spelers",
      duration: "45-55 minuten",
      difficulty: "Medium",
      age: "10+",
      space: "5m x 5.5m free-roam ruimte",
      intensity: "Low-Medium",
    },
  },
  {
    slug: "hunter-vr",
    title: "HunterVR",
    description: "Test je precisie in deze competitieve jacht simulator",
    fullDescription:
      "HunterVR brengt realistische jacht naar virtual reality. Verken prachtige natuurlijke omgevingen, track wilde dieren en test je precisie met verschillende wapens. Deze competitieve simulator is snel, fysiek en vol actie. Met tot 10 spelers kunnen teams tegen elkaar strijden in verschillende hunting challenges. Leer tracking skills, beheer je adem voor de perfecte shot en ervaar de spanning van de jacht zonder de echte wereld te verlaten. Perfect voor teambuilding events en competitieve groepen.",
    image: "/src/assets/hunter-vr.jpg",
    youtubeId: "g4XmkKdh_O4",
    features: [
      "2-6 spelers competitief",
      "Realistische jacht mechanics",
      "Verschillende wapentypes",
      "Prachtige natuuromgevingen",
      "Tracking en stealth gameplay",
      "Precision shooting",
    ],
    highlights: [
      "Competitieve hunting experience",
      "Test je precisie vaardigheden",
      "Grote groepen support",
      "Realistische natuur simulatie",
    ],
    details: {
      players: "2-6 spelers",
      duration: "30-45 minuten",
      difficulty: "Medium",
      age: "12+",
      space: "2m x 2m aanbevolen",
      intensity: "Medium",
    },
  },
  {
    slug: "after-the-fall",
    title: "After The Fall",
    description: "Vecht je een weg door een bevroren post-apocalyptisch LA vol hordes ondoden",
    fullDescription:
      "Van de makers van Arizona Sunshine komt After The Fall — een epische multiplayer VR actie-FPS met naadloze coöperatieve gameplay. Samen met tot vier spelers vecht je je een weg door een vijandige, bevroren wereld in een meedogenloze strijd om te overleven. Het spel speelt zich af in de met ijs bedekte ruïnes van Los Angeles, bijna 20 jaar na de apocalyps. After The Fall combineert een levendige, gevaarlijke wereld met intense first-person shooter actie. Pak je wapens, upgrade je arsenaal en werk samen om de hordes Snowbreed te verslaan. Elke missie brengt nieuwe uitdagingen en grotere gevaren.",
    image: "/src/assets/after-the-fall.jpg",
    videoSrc: "/videos/after-the-fall-trailer.mp4",
    features: [
      "1-4 spelers co-op free-roam",
      "Post-apocalyptisch bevroren LA",
      "Uitgebreid wapenarsenaal en upgrades",
      "Intense zombie survival actie",
      "Van de makers van Arizona Sunshine",
      "Naadloze multiplayer gameplay",
    ],
    highlights: [
      "Award-winning VR shooter",
      "Intense co-op ervaring",
      "Bevroren post-apocalyptische wereld",
      "Free-roam VR actie",
    ],
    details: {
      players: "1-4 spelers",
      duration: "30-45 minuten",
      difficulty: "Medium-Hard",
      age: "12+",
      space: "Free-roam speelruimte",
      intensity: "Hoog",
    },
  },
  {
    slug: "terminator-uprising",
    title: "Terminator Uprising",
    description: "Sluit je aan bij het verzet tegen Skynet in deze intense VR shooter",
    fullDescription:
      "Sluit je aan bij een groep overlevenden in de strijd tegen Skynet. In de grimmige toekomst na Judgment Day ga je op een gevaarlijke missie onder leiding van John Connor. Vol adrenaline-pompende actie word jij en je team midden op een slagveld gedropt dat overspoeld wordt door Terminators. Kies je wapens en upgrades zorgvuldig, want de toekomst van de mensheid hangt af van jouw keuzes. Deze officiële Terminator 2: Judgment Day VR-ervaring brengt de iconische franchise tot leven in free-roam virtual reality.",
    image: "/src/assets/terminator-uprising.jpg",
    youtubeId: "w1-JEMy2CDU",
    features: [
      "2-6 spelers free-roam co-op",
      "Officiële Terminator 2 licentie",
      "Intense actie en gevechten",
      "Wapen- en upgrade systeem",
      "Epische boss battles",
      "Geschikt voor beginners en ervaren spelers",
    ],
    highlights: [
      "Officiële Terminator licentie",
      "Epische sci-fi actie",
      "Grote groepen ondersteuning",
      "Intense boss fights",
    ],
    details: {
      players: "2-6 spelers",
      duration: "12 minuten",
      difficulty: "Medium",
      age: "16+",
      space: "Free-roam speelruimte",
      intensity: "Hoog",
    },
  },
  {
    slug: "cookd-up",
    title: "Cook'd Up",
    description: "Chaotisch, hilarisch en vol teamwork in deze gekke VR keuken ervaring",
    fullDescription:
      "Stap de keuken in van Cook'd Up, waar jij en je teamgenoten chef-koks zijn in een wild onvoorspelbare keuken! Hak, bak, serveer en voldoe aan belachelijke bestellingen terwijl je vuur, gemorste vloeistoffen en de druk van de dinner rush ontwijkt. Denk aan Overcooked — maar dan in volledige VR. Snel, hectisch en totaal belachelijk. Verdeel taken, werk samen en probeer zoveel mogelijk burgers te flippen voordat de dag voorbij is. Te makkelijk? Naarmate de dag vordert, worden de recepten steeds uitdagender, waardoor samenwerking cruciaal wordt. Een perfecte game voor families, vrienden en teambuilding!",
    image: "/src/assets/cookd-up.jpg",
    youtubeId: "rxvX6HY0jWM",
    features: [
      "2-6 spelers free-roam co-op",
      "Familie-vriendelijke gameplay",
      "Chaotische keuken actie",
      "Teamwork en communicatie",
      "Steeds moeilijker wordende recepten",
      "Perfect voor teambuilding",
    ],
    highlights: [
      "Familie-vriendelijk",
      "Overcooked in VR",
      "Hilarisch teamwork",
      "Geschikt voor alle leeftijden",
    ],
    details: {
      players: "2-6 spelers",
      duration: "10 minuten",
      difficulty: "Easy-Medium",
      age: "8+",
      space: "Free-roam speelruimte",
      intensity: "Medium",
    },
  },
  {
    slug: "dead-ahead",
    title: "Dead Ahead",
    description: "Omsingeld door zombies in de straten van de stad — overleef de horde!",
    fullDescription:
      "Jij en je team zijn omsingeld door zombies in de straten van de stad. Met ondoden die van alle kanten op je afkomen, pak je je wapens of spring je achter het gemonteerde machinegeweer. Je hebt elke druppel moed en teamwork nodig om te overleven totdat de hulp arriveert! Kies je personage en zwaai met een geweldig assortiment aan wapens in deze krankzinnig leuke zombie shooter. Bouw verdedigingen op tijdens de rustmomenten, want bij elke golf neemt de druk toe en worden de zombies sneller. Een perfecte keuze voor zowel beginners als ervaren spelers!",
    image: "/src/assets/dead-ahead.jpg",
    youtubeId: "pVyqGmBBW7o",
    features: [
      "2-6 spelers free-roam co-op",
      "Intense zombie survival",
      "Verdedigingen bouwen",
      "Diverse wapenkeuze",
      "Toenemende moeilijkheidsgraad",
      "Geschikt voor beginners en ervaren spelers",
    ],
    highlights: [
      "Intense zombie actie",
      "Strategische verdediging",
      "Wave-based survival",
      "Free-roam VR ervaring",
    ],
    details: {
      players: "2-6 spelers",
      duration: "10-12 minuten",
      difficulty: "Medium",
      age: "16+",
      space: "Free-roam speelruimte",
      intensity: "Hoog",
    },
  },
  {
    slug: "arrowsong",
    title: "Arrowsong",
    description: "Bescherm de heilige boom tegen orcs, goblins en een dreigende draak!",
    fullDescription:
      "Orcs, goblins en een dreigende draak willen de heilige boom vernietigen! Jij en je groep helden zijn opgeroepen om dit kwaad te stoppen en vrede te brengen in het feeënrijk. Een gevaarlijke missie die zelfs de meest ervaren avonturier zou kunnen overweldigen. Pak je boog, richt zorgvuldig en gebruik magische krachten om de aanvallende horde te verslaan. In dit coöperatieve fantasy archery avontuur werk je samen met je team om golf na golf vijanden af te slaan. Met prachtige magische omgevingen en intuïtieve boogschiet-mechanics is Arrowsong een betoverende ervaring voor het hele gezin.",
    image: "/src/assets/arrowsong.jpg",
    youtubeId: "Xwf42hKzbIg",
    features: [
      "2-6 spelers free-roam co-op",
      "Fantasy archery gameplay",
      "Magische krachten en wapens",
      "Prachtige fantasy omgevingen",
      "Wave-based coöperatief",
      "Familie-vriendelijke ervaring",
    ],
    highlights: [
      "Betoverend fantasy avontuur",
      "Intuïtief boogschieten",
      "Familie-vriendelijk",
      "Coöperatieve gameplay",
    ],
    details: {
      players: "2-6 spelers",
      duration: "10-12 minuten",
      difficulty: "Easy-Medium",
      age: "Alle leeftijden",
      space: "Free-roam speelruimte",
      intensity: "Medium",
    },
  },
  {
    slug: "cyber-shock",
    title: "Cyber Shock",
    description: "Een snelle, futuristische VR shooter vol neon, energie-wapens en non-stop actie",
    fullDescription:
      "Cyber Shock is een fast-paced futuristische VR shooter die je midden in een neon-verlichte cyberpunk wereld katapulteert. Bewapend met geavanceerde energie-wapens neem je het op tegen golven van vijanden in een visueel spectaculaire arena. Bewegen, dekken zoeken en samenwerken zijn essentieel — communiceer met je team om de hectische gevechten te overleven. Met snelle gameplay, intuïtieve controls en een explosieve cyberpunk sfeer is Cyber Shock perfect voor groepen die op zoek zijn naar adrenaline en pure VR actie. Een must-play voor fans van high-energy shooters en sci-fi werelden.",
    image: "/src/assets/cyber-shock.jpg",
    youtubeId: "RlCz-GHCyzs",
    features: [
      "2-6 spelers free-roam co-op",
      "Futuristische cyberpunk setting",
      "Snelle, hectische gameplay",
      "Energie-wapens en upgrades",
      "Wave-based actie",
      "Intuïtieve controls voor alle skill levels",
    ],
    highlights: [
      "Fast-paced futuristische shooter",
      "Spectaculaire neon visuals",
      "Adrenaline-pompende actie",
      "Free-roam co-op gameplay",
    ],
    details: {
      players: "2-6 spelers",
      duration: "10-12 minuten",
      difficulty: "Medium",
      age: "12+",
      space: "Free-roam speelruimte",
      intensity: "Hoog",
    },
  },
  {
    slug: "wizard-academy",
    title: "Wizard Academy",
    description: "Word leerling van een magische academie en beleef een betoverend co-op fantasy avontuur",
    fullDescription:
      "Welkom op de Wizard Academy! Stap binnen in een betoverende magische school waar jij en je team in opleiding zijn tot heuse tovenaars. Leer spreuken werpen, los magische puzzels op en werk samen om de uitdagingen van de academie te doorstaan. In dit kleurrijke en fantasievolle co-op avontuur maak je gebruik van handgebaren om magie te ontketenen — vuur, ijs en pure energie liggen letterlijk in jouw handen. Met sfeervolle omgevingen, intuïtieve magie-mechanics en een betoverende soundtrack is Wizard Academy de perfecte VR ervaring voor families, vrienden en teambuilding groepen die op zoek zijn naar iets unieks en fantasievols.",
    image: "/src/assets/wizard-academy.jpg",
    youtubeId: "e166JrKFv90",
    features: [
      "2-6 spelers free-roam co-op",
      "Magie via handgebaren",
      "Magische puzzels en uitdagingen",
      "Betoverende fantasy omgevingen",
      "Familie-vriendelijk avontuur",
      "Perfect voor teambuilding",
    ],
    highlights: [
      "Betoverend fantasy avontuur",
      "Unieke magie mechanics",
      "Familie-vriendelijk",
      "Coöperatieve gameplay",
    ],
    details: {
      players: "2-6 spelers",
      duration: "10-12 minuten",
      difficulty: "Easy-Medium",
      age: "8+",
      space: "Free-roam speelruimte",
      intensity: "Medium",
    },
  },
  {
    slug: "wayfinders",
    title: "Wayfinders",
    description: "Ontsnap uit Aurora in dit betoverend co-op avontuur vol mysterieuze landschappen en uitdagende puzzels",
    fullDescription:
      "Wayfinders: Escape from Aurora neemt je mee op een betoverende reis door mysterieuze, ongerepte landschappen. Jij en je team zijn ontdekkingsreizigers die vastzitten in het verborgen rijk van Aurora. Om te ontsnappen moet je samenwerken om oude mechanismen te activeren, uitdagende puzzels op te lossen en verborgen paden te onthullen. Elk landschap biedt unieke uitdagingen die communicatie en teamwork vereisen. Met prachtige, dromerige visuals en een meeslepende sfeer is Wayfinders een uniek avontuur dat perfect is voor families, vriendengroepen en teambuilding evenementen die op zoek zijn naar iets anders dan de standaard shooter ervaring.",
    image: "/src/assets/wayfinders.jpg",
    youtubeId: "iotxmLcCR6w",
    features: [
      "2-6 spelers free-roam co-op",
      "Betoverende, mystieke landschappen",
      "Uitdagende teamwork puzzels",
      "Unieke ontdekkingsmechanics",
      "Familie-vriendelijk avontuur",
      "Perfect voor teambuilding",
    ],
    highlights: [
      "Betoverend avontuur",
      "Unieke puzzel-ervaring",
      "Familie-vriendelijk",
      "Coöperatieve ontdekking",
    ],
    details: {
      players: "2-6 spelers",
      duration: "10-12 minuten",
      difficulty: "Easy-Medium",
      age: "8+",
      space: "Free-roam speelruimte",
      intensity: "Low-Medium",
    },
  },
];

const GameDetail = () => {
  const { gameSlug } = useParams();
  const navigate = useNavigate();

  const game = gamesData.find((g) => g.slug === gameSlug);

  useEffect(() => {
    if (game) {
      trackEvent(AnalyticsEvents.GAME_DETAIL_VIEW, { game: game.slug, title: game.title });
    }
  }, [game?.slug]);

  if (!game) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold mb-4">Game niet gevonden</h1>
          <Button onClick={() => navigate("/experiences")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar overzicht
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const scrollToContact = () => {
    navigate("/#contact-form");
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple/20 via-background to-background" />
        <div className="container mx-auto max-w-6xl relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 animate-fade-in">
            <Link to="/" className="hover:text-foreground transition-colors flex items-center gap-1">
              <Home className="h-4 w-4" />
              Home
            </Link>
            <span>/</span>
            <Link to="/experiences" className="hover:text-foreground transition-colors">
              Experiences
            </Link>
            <span>/</span>
            <span className="text-foreground">{game.title}</span>
          </nav>

          <Button
            variant="outline"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/experiences"))}
            className="mb-8 animate-fade-in"
            style={{ animationDelay: "0.1s" }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Terug naar overzicht
          </Button>

          <h1
            className="text-4xl md:text-6xl font-bold mb-6 text-primary animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            {game.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl animate-fade-in" style={{ animationDelay: "0.3s" }}>
            {game.description}
          </p>
        </div>
      </section>

      {/* Video Preview Section */}
      <section className="py-12 px-4 bg-background/50">
        <div className="container mx-auto max-w-5xl animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <h2 className="text-3xl font-bold mb-6">Game Preview</h2>
          {game.youtubeId || game.videoSrc ? (
            <VideoEmbed youtubeId={game.youtubeId} videoSrc={game.videoSrc} title={game.title} />
          ) : (
            <img src={game.image} alt={game.title} className="w-full aspect-video object-cover rounded-lg" />
          )}
        </div>
      </section>

      {/* Game Information Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Description */}
            <div className="md:col-span-2 space-y-8">
              <Card
                className="bg-card/50 backdrop-blur-sm border-border animate-fade-in"
                style={{ animationDelay: "0.5s" }}
              >
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4">Over de Game</h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{game.fullDescription}</p>
                </CardContent>
              </Card>

              <Card
                className="bg-card/50 backdrop-blur-sm border-border animate-fade-in"
                style={{ animationDelay: "0.6s" }}
              >
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Features</h3>
                  <ul className="grid md:grid-cols-2 gap-3">
                    {game.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-primary mt-1">✓</span>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card
                className="bg-card/50 backdrop-blur-sm border-border animate-fade-in"
                style={{ animationDelay: "0.7s" }}
              >
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Hoogtepunten</h3>
                  <div className="flex flex-wrap gap-2">
                    {game.highlights.map((highlight, index) => (
                      <Badge key={index} variant="secondary" className="text-sm py-1 px-3">
                        {highlight}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar with Details */}
            <div className="space-y-6">
              <Card
                className="bg-card/50 backdrop-blur-sm border-border animate-fade-in"
                style={{ animationDelay: "0.8s" }}
              >
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Specificaties</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">Spelers</p>
                        <p className="text-muted-foreground text-sm">{game.details.players}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">Speelduur</p>
                        <p className="text-muted-foreground text-sm">{game.details.duration}</p>
                      </div>
                    </div>
                    <div className="border-t border-border pt-4 mt-4">
                      <p className="text-sm font-medium mb-2">Extra Info</p>
                      <p className="text-xs text-muted-foreground mb-1">
                        <span className="font-medium">Leeftijd:</span> {game.details.age}
                      </p>
                      <p className="text-xs text-muted-foreground mb-1">
                        <span className="font-medium">Ruimte:</span> {game.details.space}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Intensiteit:</span> {game.details.intensity}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="bg-primary p-6 text-primary-foreground animate-fade-in"
                style={{ animationDelay: "0.9s" }}
              >
                <h3 className="text-xl font-bold mb-3">Interesse?</h3>
                <p className="text-sm mb-4 opacity-90">Boek deze experience voor je volgende event!</p>
                <Button onClick={() => {
                  trackEvent(AnalyticsEvents.BOOK_NOW, { game: game.slug, location: 'game_detail_sidebar' });
                  scrollToContact();
                }} variant="secondary" className="w-full">
                  Neem Contact Op
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GameDetail;
